import { useState, useMemo, useEffect } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import {
  useGetMyBookingsQuery,
  useGetCancelledBookingsAsGuestQuery,
} from '../../services/bookingService/__generated__/queries.generated';
import { URLS } from '../../../constants/urls';
import _ from 'lodash';
import { format } from 'date-fns';
import { formatInstantInZone } from '../../helpers/timezoneHelpers';
import { useNavigate } from 'react-router-dom';
import { MyBookingsPageSkeleton } from '../../components/skeletons';
import { getShortIdFromUuid, formatShortId } from '../../utils/short-id';
import { Colors } from '../../styles/colors';
import { formatBookingLocationDisplay } from '../../helpers/formatAddress';
const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 box-border p-4 bg-bg-secondary min-h-screen pb-8 md:pb-16`}
`;

const Title = styled.h1`
  ${tw`lg:text-3xl text-2xl font-bold mb-6 text-text-primary px-4 md:px-8 lg:px-0 max-w-screen-xl mx-auto lg:py-5 md:py-3 py-2`}
`;

const BookingList = styled.div`
  ${tw`space-y-4 max-w-screen-xl mx-auto px-4 md:px-8 lg:px-0`}
`;

const BookingCard = styled.div`
  ${tw`bg-white rounded-lg shadow-card`}
`;

const BookingHeader = styled.div`
  ${tw`p-4 border-b border-border-default`}
`;

const BookingBody = styled.div`
  ${tw`p-4`}
`;

const DateRange = styled.div`
  ${tw`text-sm text-text-secondary mb-2`}
`;

const CarInfo = styled.div`
  ${tw`flex items-center gap-4`}
`;

const CarImage = styled.img`
  ${tw`w-20 h-16 rounded-md object-cover`}
`;

const CarDetails = styled.div`
  ${tw`flex-1`}
`;

const CarName = styled.h3`
  ${tw`text-lg font-medium`}
`;

const Location = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const Lable = styled.span`
  ${tw`font-bold`}
`;

const Status = styled.span<{ status: string }>`
  ${tw`px-2 py-1 rounded-full text-xs font-medium max-w-full min-w-0 box-border`}
  ${({ status }) => {
    switch (status) {
      case 'CONFIRMED':
      case 'BOOKED':
        return `background-color: #ECFDF5; color: #065F46;`;
      case 'PENDING':
        return `background-color: #FFFBEB; color: #92400E;`;
      case 'COMPLETED':
        return `background-color: ${Colors.Gray100}; color: #374151;`;
      case 'IN_PROGRESS':
        return `background-color: ${Colors.Gray100}; color: ${Colors.Black};`;
      case 'CANCELLED':
      case 'CARD_DECLINED':
      default:
        return `background-color: #FEF2F2; color: #991B1B;`;
    }
  }}
`;

const ShortIdBadge = styled.span`
  ${tw`px-2 py-0.5 rounded-full text-xs font-semibold ml-2 inline-flex items-center uppercase`}
  background-color: ${Colors.Gray100};
  color: ${Colors.Black};
`;

const HostInfo = styled.div`
  ${tw`mt-3 pt-3 border-t border-border-default`}
`;

const HostLabel = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const HostName = styled.span`
  ${tw`text-sm text-text-secondary ml-1`}
`;

const SwapIndicator = styled.div`
  ${tw`mt-2 p-2 rounded-md text-sm`}
  background-color: ${Colors.Gray100};
  border: 1px solid ${Colors.Gray200};
`;

const SwapLabel = styled.span`
  ${tw`font-semibold`}
  color: ${Colors.TextPrimary};
`;

const TabContainer = styled.div`
  ${tw`flex mb-6 justify-center rounded-lg p-1`}
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  width: fit-content;
  margin: 0 auto;
  margin-bottom: 30px;
`;

const Tab = styled.button<{ active: boolean }>`
  ${tw`px-6 py-2 text-sm font-medium transition-all duration-200 border-none rounded-md cursor-pointer`}
  ${({ active }) =>
    active
      ? tw`bg-bg-elevated text-text-primary shadow-xs`
      : css`
          ${tw`bg-transparent text-text-secondary`}
          &:hover {
            ${tw`text-text-primary`}
          }
        `}
`;

const SortContainer = styled.div`
  ${tw`flex items-center justify-center gap-2 mb-6`}
`;

const SortLabel = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const SortTag = styled.button<{ active: boolean }>`
  ${tw`px-4 py-1.5 text-xs font-medium rounded-full border transition-all duration-200`}
  ${({ active }) =>
    active
      ? `background-color: ${Colors.Black}; color: white; border-color: ${Colors.Black};`
      : `background-color: white; color: #4b5563; border-color: #d1d5db;`}
  &:hover {
    border-color: ${Colors.Black};
  }
`;

const SearchResultContainer = styled.div`
  ${tw`flex flex-col mt-20 text-center max-w-screen-xl mx-auto`}
`;

const MyBookingsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'ongoing' | 'history' | 'cancelled'>(
    'upcoming',
  );
  const [cancelSort, setCancelSort] = useState<'cancelledAt' | 'startAt'>('cancelledAt');
  const { loading, error, data } = useGetMyBookingsQuery({ fetchPolicy: 'network-only' });

  const { data: cancelledData, loading: cancelledLoading } = useGetCancelledBookingsAsGuestQuery({
    skip: activeTab !== 'cancelled',
    fetchPolicy: 'network-only',
  });
  const today = new Date();
  // Locations are now included directly in the booking response, no need to fetch separately

  const formatBookingLocation = (pickupOrReturnLocation: any, carLocation: any) =>
    formatBookingLocationDisplay(pickupOrReturnLocation, carLocation);

  const filteredBookings = useMemo(() => {
    if (!data?.myBookings) return [];

    const filtered = data.myBookings.filter(booking => {
      const startDate = new Date(booking.startAt);
      const endDate = new Date(booking.endAt);
      const isCancelled = booking.status?.includes('CANCELLED');

      switch (activeTab) {
        case 'upcoming':
          return !isCancelled && startDate >= today;
        case 'ongoing':
          return !isCancelled && startDate <= today && endDate >= today;
        case 'history':
          return !isCancelled && endDate < today;
        default:
          return false;
      }
    });

    // Sort bookings based on tab
    return filtered.sort((a, b) => {
      if (activeTab === 'history') {
        // History: sort by endAt DESC (most recently finished first)
        return b.endAt - a.endAt;
      } else {
        // Upcoming & Ongoing: sort by startAt ASC (earliest first)
        return a.startAt - b.startAt;
      }
    });
  }, [data?.myBookings, activeTab]);

  const sortedCancelledBookings = useMemo(() => {
    const list = cancelledData?.cancelledBookingsAsGuest ?? [];
    return [...list].sort((a, b) =>
      cancelSort === 'cancelledAt'
        ? new Date((b as any).cancelledInitiatedAt ?? 0).getTime() -
          new Date((a as any).cancelledInitiatedAt ?? 0).getTime()
        : b.startAt - a.startAt,
    );
  }, [cancelledData, cancelSort]);

  if (loading) {
    return <MyBookingsPageSkeleton />;
  }

  if (error) {
    return <PageContainer>Error loading your bookings.</PageContainer>;
  }

  return (
    <PageContainer>
      <Title>My Bookings</Title>
      <TabContainer>
        <Tab active={activeTab === 'upcoming'} onClick={() => setActiveTab('upcoming')}>
          Upcoming
        </Tab>
        <Tab active={activeTab === 'ongoing'} onClick={() => setActiveTab('ongoing')}>
          Ongoing
        </Tab>
        <Tab active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
          History
        </Tab>
        <Tab active={activeTab === 'cancelled'} onClick={() => setActiveTab('cancelled')}>
          Cancelled
        </Tab>
      </TabContainer>

      {activeTab === 'cancelled' && (
        <SortContainer>
          <SortLabel>Sort by:</SortLabel>
          <SortTag
            active={cancelSort === 'cancelledAt'}
            onClick={() => setCancelSort('cancelledAt')}
          >
            Cancellation Date
          </SortTag>
          <SortTag active={cancelSort === 'startAt'} onClick={() => setCancelSort('startAt')}>
            Trip Start Date
          </SortTag>
        </SortContainer>
      )}

      {activeTab === 'cancelled' ? (
        cancelledLoading ? (
          <SearchResultContainer>Loading cancelled bookings...</SearchResultContainer>
        ) : !sortedCancelledBookings.length ? (
          <SearchResultContainer>No cancelled bookings found.</SearchResultContainer>
        ) : (
          <BookingList>
            {sortedCancelledBookings.map(booking => (
              <BookingCard
                key={booking.id}
                onClick={() => navigate(`/cars/guest/booking/${booking.id}`)}
              >
                <BookingHeader>
                  <div tw="flex justify-between items-center">
                    <div tw="flex items-center">
                      <Status status={booking.status}>{booking.status.replace(/_/g, ' ')}</Status>
                      <ShortIdBadge>
                        ID:{' '}
                        {formatShortId((booking as any).shortId || getShortIdFromUuid(booking.id))}
                      </ShortIdBadge>
                    </div>
                    <span tw="text-xs text-text-secondary">
                      {(booking as any).cancellationReason?.replace(/_/g, ' ')}
                    </span>
                  </div>
                </BookingHeader>
                <BookingBody>
                  <DateRange>
                    {formatInstantInZone(
                      booking.startAt,
                      booking.car?.location?.timezone ?? 'America/Los_Angeles',
                      'MM/dd/yyyy hh:mm a',
                    )}{' '}
                    -{' '}
                    {formatInstantInZone(
                      booking.endAt,
                      booking.car?.location?.timezone ?? 'America/Los_Angeles',
                      'MM/dd/yyyy hh:mm a',
                    )}
                  </DateRange>
                  {(booking as any).cancelledInitiatedAt && (
                    <DateRange tw="text-red-500">
                      Cancelled:{' '}
                      {format(
                        new Date((booking as any).cancelledInitiatedAt),
                        'MM/dd/yyyy hh:mm a',
                      )}
                    </DateRange>
                  )}
                  <CarInfo>
                    <CarImage
                      src={
                        `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${booking.car?.thumbnailUrl}` ||
                        '/placeholder-car.png'
                      }
                      alt="Car"
                    />
                    <CarDetails>
                      <CarName>{`${booking.car?.year} ${booking.car?.make} ${booking.car?.model}`}</CarName>
                      <Location>
                        <Lable>pickup location:</Lable>{' '}
                        {formatBookingLocation(
                          (booking as any).pickupLocation,
                          (booking as any).car?.location,
                        )}
                      </Location>
                      <Location>
                        <Lable>return location:</Lable>{' '}
                        {formatBookingLocation(
                          (booking as any).returnLocation,
                          (booking as any).car?.location,
                        )}
                      </Location>
                      <HostInfo>
                        <HostLabel>Host:</HostLabel>
                        <HostName>
                          {(booking as any).hostDisplayName ||
                            booking.host?.displayName ||
                            `${booking.host?.firstName} ${booking.host?.lastName}` ||
                            'Deleted User'}
                        </HostName>
                      </HostInfo>
                    </CarDetails>
                  </CarInfo>
                </BookingBody>
              </BookingCard>
            ))}
          </BookingList>
        )
      ) : !filteredBookings.length ? (
        <SearchResultContainer>No bookings found.</SearchResultContainer>
      ) : (
        <BookingList>
          {filteredBookings.map(booking => (
            <BookingCard
              key={booking.id}
              onClick={() => {
                navigate(`/cars/guest/booking/${booking.id}`);
              }}
            >
              <BookingHeader>
                <div tw="flex justify-between items-center">
                  <div tw="flex items-center">
                    <Status status={booking.status}>{booking.status}</Status>
                    <ShortIdBadge>
                      ID:{' '}
                      {formatShortId((booking as any).shortId || getShortIdFromUuid(booking.id))}
                    </ShortIdBadge>
                  </div>
                </div>
              </BookingHeader>
              <BookingBody>
                <DateRange>
                  {formatInstantInZone(
                    booking.startAt,
                    booking.car?.location?.timezone ?? 'America/Los_Angeles',
                    'MM/dd/yyyy hh:mm a',
                  )}{' '}
                  -{' '}
                  {formatInstantInZone(
                    booking.endAt,
                    booking.car?.location?.timezone ?? 'America/Los_Angeles',
                    'MM/dd/yyyy hh:mm a',
                  )}
                </DateRange>
                <CarInfo>
                  <CarImage
                    src={
                      `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${booking.car?.thumbnailUrl}` ||
                      '/placeholder-car.png'
                    }
                    alt="Car"
                  />
                  <CarDetails>
                    <CarName>{`${booking.car?.year} ${booking.car?.make} ${booking.car?.model}`}</CarName>
                    {(booking as any).swapHistory && (booking as any).swapHistory.length > 0 && (
                      <SwapIndicator>
                        <SwapLabel>
                          {(booking as any).swapHistory.length === 1
                            ? 'Swapped 1 time'
                            : `Swapped ${(booking as any).swapHistory.length} times`}
                          {(booking as any).originalCar && (
                            <>
                              {' '}
                              • Original: {(booking as any).originalCar.year}{' '}
                              {(booking as any).originalCar.make}{' '}
                              {(booking as any).originalCar.model}
                            </>
                          )}
                        </SwapLabel>
                      </SwapIndicator>
                    )}
                    <Location>
                      <Lable>pickup location:</Lable>{' '}
                      {formatBookingLocation(
                        (booking as any).pickupLocation,
                        (booking as any).car?.location,
                      )}
                    </Location>
                    <Location>
                      <Lable>return location:</Lable>{' '}
                      {formatBookingLocation(
                        (booking as any).returnLocation,
                        (booking as any).car?.location,
                      )}
                    </Location>
                    <HostInfo>
                      <HostLabel>Host:</HostLabel>
                      <HostName>
                        {booking.hostDisplayName ||
                          booking.host?.displayName ||
                          `${booking.host?.firstName} ${booking.host?.lastName}` ||
                          'Deleted User'}
                      </HostName>
                    </HostInfo>
                  </CarDetails>
                </CarInfo>
              </BookingBody>
            </BookingCard>
          ))}
        </BookingList>
      )}
    </PageContainer>
  );
};

export default MyBookingsPage;
